// client/src/coms/presence/PresenceStatusSetting.js
// Settings → Privacy & Security → Status: choose Online, Idle / AFK, Do Not Disturb or Appear offline, for any
// duration (or until you turn it off), see the time left, change or cancel it, and set a custom status message.
// Everything is stored on the server, so it is the same on desktop, phone and browser and survives closing them.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row } from '../personalisation/settingsControls';
import { DurationPicker, StatusDot, STATUS_META } from './PresenceUi';
import { DEFAULT_DURATION, durationError, formatEnds, formatRemaining, toApiDuration } from './durations';
import { PRESENCE_EVENT, getMyStatus, setMyStatus, setMyStatusText, usePresence } from './presenceClient';

const CHOICES = [
  { value: 'online', title: 'Online', hint: 'Others see you as online while Voxemera is open.' },
  { value: 'idle', title: 'Idle / AFK', hint: 'A moon and “AFK” next to your name.' },
  { value: 'dnd', title: 'Do Not Disturb', hint: 'Red status; no message or DM notifications on any device.' },
  { value: 'invisible', title: 'Appear offline', hint: 'Everyone sees you as offline. You can still use everything.' },
];

export function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function describeMine(me, now, skewMs = 0) {
  if (!me) return '';
  const label = STATUS_META[me.status] ? STATUS_META[me.status].label : 'Online';
  if (me.status === 'online') return 'Online';
  if (!me.until_ms) return `${label}, until you turn it off`;
  const left = me.until_ms - (now + skewMs);
  return `${label}, ${formatRemaining(left)} left (ends ${formatEnds(me.until_ms)})`;
}

export default function PresenceStatusSetting() {
  const presence = usePresence();
  const now = useNow(15000);
  const [me, setMe] = useState(null);
  const [choice, setChoice] = useState('online');
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [text, setText] = useState('');
  const [textSaved, setTextSaved] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  // Set once the user starts editing: a late load or a background re-read never overwrites their choice.
  const dirty = useRef(false);
  const edit = (setter) => (v) => {
    dirty.current = true;
    setter(v);
  };

  const adopt = useCallback((s, { resetDraft = false, force = false } = {}) => {
    if (!s) return;
    setMe(s);
    setTextSaved(s.custom_text || '');
    if (resetDraft && (force || !dirty.current)) {
      setChoice(s.status);
      setText(s.custom_text || '');
      if (s.status !== 'online') setDuration((d) => ({ ...d, forever: !s.until_ms }));
    }
  }, []);

  useEffect(() => {
    let alive = true;
    getMyStatus()
      .then((s) => alive && adopt(s, { resetDraft: true }))
      .catch((e) => alive && setError(e.message));
    function onChange(e) {
      if (e && e.detail && e.detail.status) adopt(e.detail);
    }
    window.addEventListener(PRESENCE_EVENT, onChange);
    return () => {
      alive = false;
      window.removeEventListener(PRESENCE_EVENT, onChange);
    };
  }, [adopt]);

  // Changes made on another device arrive through the shared poll.
  const polledMe = presence.me;
  useEffect(() => {
    if (polledMe) setMe((cur) => (cur && cur.status === polledMe.status && cur.until_ms === polledMe.until_ms && cur.custom_text === polledMe.custom_text ? cur : polledMe));
  }, [polledMe]);

  // A timed status that runs out while this page is open: re-read it at the moment it ends.
  useEffect(() => {
    if (!me || !me.until_ms) return undefined;
    const wait = me.until_ms - (Date.now() + (presence.skewMs || 0)) + 1500;
    if (wait > 2147483000) return undefined;
    const t = setTimeout(() => getMyStatus().then((s) => adopt(s, { resetDraft: true })).catch(() => {}), Math.max(1000, wait));
    return () => clearTimeout(t);
  }, [me, presence.skewMs, adopt]);

  async function run(fn, okNote) {
    setBusy(true);
    setError('');
    setNote('');
    try {
      const s = await fn();
      dirty.current = false;
      adopt(s, { resetDraft: true, force: true });
      if (okNote) setNote(okNote);
    } catch (e) {
      setError(`Your status was not changed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  const durErr = choice === 'online' ? '' : durationError(duration);
  const apply = () => run(() => setMyStatus(choice, choice === 'online' ? null : toApiDuration(duration)), 'Status saved.');
  const cancel = () => run(() => setMyStatus('online'), 'Back to Online.');
  const saveText = () => run(() => setMyStatusText(text), text.trim() ? 'Status message saved.' : 'Status message cleared.');
  const clearText = () => {
    setText('');
    run(() => setMyStatusText(''), 'Status message cleared.');
  };

  return (
    <>
      <Row label="Status" hint="How others see you in member lists and voice channels. Saved to your account, so it is the same on every device." settingId="status" scope="account">
        <div className="vx-pres-setting" data-testid="presence-setting">
          <div className="vx-pres-current" role="status" data-testid="presence-current">
            <StatusDot status={me ? me.status : 'online'} size={12} />
            <span>
              <strong>Now:</strong> {me ? describeMine(me, now, presence.skewMs) : 'Loading…'}
            </span>
            {me && me.status !== 'online' ? (
              <button type="button" className="vx-btn small" disabled={busy} onClick={cancel} data-testid="presence-cancel">
                {me.status === 'invisible' ? 'Stop appearing offline' : 'Back to Online'}
              </button>
            ) : null}
          </div>

          <div className="vx-pres-choices" role="radiogroup" aria-label="Status">
            {CHOICES.map((c) => (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={choice === c.value}
                className={`vx-pres-choice${choice === c.value ? ' is-on' : ''}`}
                onClick={() => edit(setChoice)(c.value)}
                disabled={busy}
                data-testid={`presence-choice-${c.value}`}
              >
                <StatusDot status={c.value} size={12} />
                <span>
                  <span className="vx-pres-choice-title">{c.title}</span>
                  <span className="vx-pres-choice-hint">{c.hint}</span>
                </span>
              </button>
            ))}
          </div>

          {choice !== 'online' ? <DurationPicker value={duration} onChange={edit(setDuration)} disabled={busy} /> : null}

          <div className="vx-pres-actions">
            <button type="button" className="vx-btn small primary" disabled={busy || !!durErr || !me} onClick={apply} data-testid="presence-apply">
              {busy ? 'Saving…' : me && me.status === choice && choice !== 'online' ? 'Update duration' : 'Set status'}
            </button>
            {note ? <span className="vx-pres-ok">{note}</span> : null}
          </div>
          {error ? (
            <div className="vx-acct-error" role="alert">
              {error}
            </div>
          ) : null}
        </div>
      </Row>
      <Row label="Custom status message" hint="Shown next to your name to people who can see you online. Hidden while you appear offline." settingId="status-message" scope="account">
        <div className="vx-pres-textrow">
          <input
            className="vx-pres-text"
            value={text}
            maxLength={128}
            placeholder="e.g. Back after lunch"
            aria-label="Custom status message"
            onChange={(e) => edit(setText)(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) saveText();
            }}
            data-testid="presence-text"
          />
          <button type="button" className="vx-btn small" disabled={busy || text.trim() === textSaved} onClick={saveText} data-testid="presence-text-save">
            Save
          </button>
          {textSaved ? (
            <button type="button" className="vx-btn small" disabled={busy} onClick={clearText}>
              Clear
            </button>
          ) : null}
        </div>
      </Row>
    </>
  );
}
